/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Alert, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ICookieComplianceViewProps } from '@msdyn365-commerce-modules/cookie-compliance/src/modules/cookie-compliance/./cookie-compliance';
import { ICookieComplianceConfig } from '../definition-extensions/cookie-compliance.ext.props.autogenerated';

declare global {
    interface Window {
        CookieConsent: {
            consent: {
                necessary: boolean;
            };
        };
    }
}

const getId = (id: string) => document.getElementById(id);

const showAndHideCookieButtons = () => {
    const AllowSelection = getId('CybotCookiebotDialogBodyLevelButtonLevelOptinAllowallSelection');
    const AllowAll = getId('CybotCookiebotDialogBodyLevelButtonLevelOptinAllowAll');

    if (!AllowSelection || !AllowAll) {
        return false;
    }

    AllowSelection.classList.add('hidden');

    const agreements = [
        getId('CybotCookiebotDialogBodyLevelButtonNecessary') as HTMLInputElement,
        getId('CybotCookiebotDialogBodyLevelButtonPreferences') as HTMLInputElement,
        getId('CybotCookiebotDialogBodyLevelButtonStatistics') as HTMLInputElement,
        getId('CybotCookiebotDialogBodyLevelButtonMarketing') as HTMLInputElement
    ];

    if (agreements.length === 0) {
        return false;
    }

    agreements.forEach(el => {
        if (!el) {
            return;
        }

        el.addEventListener('click', () => {
            const areAllAgreementsTrue = agreements.filter(({ checked }) => checked).length === 4;

            if (areAllAgreementsTrue) {
                AllowSelection.classList.add('hidden');
                AllowAll.classList.remove('hidden');
            } else {
                AllowSelection.classList.remove('hidden');
                AllowAll.classList.add('hidden');
            }
        });
    });

    return true;
};

const CookieComplianceView: React.FC<ICookieComplianceViewProps & ICookieComplianceConfig> = props => {
    const { CookieComplianceBanner, AlertProps, Content, acceptButton, text, links, config, context } = props;

    const { externalConsent = false } = config as ICookieComplianceConfig;

    const [areListenersAdded, setAreListenersAdded] = React.useState(false);

    React.useEffect(() => {
        if (externalConsent) {
            const consentChoosed = () => {
                if (context && context.request && context.request.cookies) {
                    context.request.cookies.setConsentCookie();
                    location.reload();
                }
            };
            window.addEventListener(
                'CookiebotOnAccept',
                () => {
                    window.CookieConsent.consent.necessary && consentChoosed();
                },
                false
            );
        }
    }, []);

    React.useEffect(() => {
        setTimeout(() => {
            if (!areListenersAdded) {
                setAreListenersAdded(showAndHideCookieButtons());
            }
        }, 0);
    });

    return (
        (!externalConsent && (
            <Module {...CookieComplianceBanner}>
                <Alert className={AlertProps.className} color={AlertProps.color} fade={AlertProps.fade}>
                    <Node {...Content}>
                        {text}
                        {links}
                    </Node>
                    {acceptButton}
                </Alert>
            </Module>
        )) || <></>
    );
};
export default CookieComplianceView;
